import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Reset } from '@zerops/fe/core';
import { Error, RemoveError } from '@zerops/fe/ngrx';
import { TranslateService } from '@ngx-translate/core';
import {
  map,
  tap,
  filter,
  switchMap,
  withLatestFrom,
  catchError,
  delay
} from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import * as Sentry from '@sentry/browser';
import { Go } from '@app/common/ngrx-router';
import { SnackService } from '@app/common/snack';
import { ErrorTranslationService } from '@app/services';
import { State } from '@app/models';
import {
  ActionTypes,
  SetActiveClient,
  CheckSavedToken,
  Authorize,
  InvalidateAuthorization,
  SetLanguage,
  SendPasswordChangeRequest,
  SendPasswordChangeLocalSuccess,
  SendPasswordChangeFail,
  ResetReferer,
  SetReferer,
  ExternalAuthConnectRequest,
  ExternalAuthConnectFail,
  ExternalAuthConnectLocalSuccess,
  ExternalAuthConnectValidateRequest,
  ExternalAuthConnectValidateLocalSuccess,
  ExternalAuthConnectValidateFail,
  ExternalAuthDeleteRequest,
  ExternalAuthDeleteLocalSuccess,
  ExternalAuthDeleteFail,
  ExternalAuthLoginRequest,
  ExternalAuthLoginLocalSuccess,
  ExternalAuthLoginFail,
  ExternalAuthLoginValidateRequest,
  ExternalAuthLoginValidateLocalSuccess,
  ExternalAuthLoginValidateFail
} from './auth-base.action';
import {
  saveAuth,
  getStoredAuth,
  saveSelectedClient,
  getSelectedClient,
  removeStoredAuthAndClient,
  updateStoredEntities,
  saveReferralClient
} from './auth-base.utils';
import { AuthBaseApi } from './auth-base.api';
import { identity, referer } from './auth-base.selector';

@Injectable()
export class AuthBaseEffect {

  private _onAuthorize$ = this._actions$.pipe(
    ofType<Authorize>(ActionTypes.Authorize)
  );
  private _externalAuthConnectValidateSuccess$ = this._actions$.pipe(
    ofType<ExternalAuthConnectValidateLocalSuccess>(ActionTypes.ExternalAuthConnectValidateLocalSuccess)
  );
  private _externalAuthConnectValidateFail$ = this._actions$.pipe(
    ofType<ExternalAuthConnectValidateFail>(ActionTypes.ExternalAuthConnectValidateFail)
  );
  private _externalAuthLoginValidateSuccess$ = this._actions$.pipe(
    ofType<ExternalAuthLoginValidateLocalSuccess>(ActionTypes.ExternalAuthLoginValidateLocalSuccess)
  );
  private _externalAuthLoginValidateFail$ = this._actions$.pipe(
    ofType<ExternalAuthLoginValidateFail>(ActionTypes.ExternalAuthLoginValidateFail)
  );
  private _onExternalAuthDeleteSuccess$ = this._actions$.pipe(
    ofType<ExternalAuthDeleteLocalSuccess>(ActionTypes.ExternalAuthDeleteLocalSuccess)
  );

  onCheckSavedToken$ = createEffect(() => this._actions$.pipe(
    ofType<CheckSavedToken>(ActionTypes.CheckSavedToken),
    map(() => {
      const data = getStoredAuth();

      const pathParts = window.location.pathname.split('/').filter((part) => !!part.length);

      const clientId = (pathParts.length > 1 && pathParts[0] === 'payment')
        ? pathParts[1]
        : getSelectedClient();

      // user was not logged in
      // or didn't select the clientId
      if (!data || !data.auth.accessToken || !clientId) {
        return new InvalidateAuthorization();
      }

      // authorize is handled inside "login-as-client"
      if (window.location.href.includes('qmsxauweag')) { return; }

      return new Authorize(
        data.entities,
        data.userId,
        data.auth,
        clientId,
        false
      );
    }),
    filter((action) => !!action)
  ));

  // set referer when accessing page unauthorized
  // window.location.pathname is the only way to get the referer path at this point
  onCheckSavedTokenFailSetReferer$ = createEffect(() => this._actions$.pipe(
    ofType<CheckSavedToken>(ActionTypes.CheckSavedToken),
    map(() => {
      const data = getStoredAuth();
      const clientId = getSelectedClient();

      const pathParts = window.location.pathname.split('/').filter((part) => !!part.length);
      const reservedPaths = ['login', 'password-recovery', 'forgotten-password', 'invitation'];

      if (pathParts.length > 1 && pathParts[0] === 'payment') {
        saveReferralClient(pathParts[1]);
      }

      if (
        (!data || !data.auth.accessToken || !clientId)
        && !reservedPaths.some((reservedPath) => pathParts.includes(reservedPath))
      ) {
        return new SetReferer(window.location.pathname);
      }

    }),
    filter((action) => !!action)
  ));

  onAuthorizeUpdateLocalStorage$ = createEffect(() => this._onAuthorize$.pipe(
    tap(({ payload }) => {
      saveAuth(payload.auth, payload.entities, payload.userId);
      if (payload.clientId) {
        saveSelectedClient(payload.clientId);
      }
    }),
    withLatestFrom(this._store.pipe(select(referer))),
    map(([ { payload }, refererPath ]) => payload.redirect
      ? new Go({ path: refererPath ? [ refererPath ] : [ '/dashboard' ] })
      : undefined
    ),
    filter((action) => !!action)
  ));

  onAuthorizeSetLanguage$ = createEffect(() => this._onAuthorize$.pipe(
    map(({ payload }) => new SetLanguage(payload.user.language.id))
  ));

  onAuthorizeSetSentryMeta$ = createEffect(() => this._onAuthorize$.pipe(
    tap(({ payload }) => {
      Sentry.configureScope((scope) => {
        scope.setUser({ 'email': payload.user.email });
      });
    })
  ), { dispatch: false });

  onSetLanguage$ = createEffect(() => this._actions$.pipe(
    ofType<SetLanguage>(ActionTypes.SetLanguage),
    tap(({ payload }) => {
      this._translate.use(payload);
    })
  ), { dispatch: false });

  onInvalidateAuthorizationRedirect$ = createEffect(() => this._actions$.pipe(
    ofType<InvalidateAuthorization>(ActionTypes.InvalidateAuthorization),
    map(({ payload }) => payload
      ? new Go({ path: ['/login'] })
      : undefined
    ),
    filter((action) => !!action)
  ));

  // sent post to delete access token from REDIS then delete auth
  onInvalidateAuthorizationApiLogout$ = createEffect(() => this._actions$.pipe(
    ofType<InvalidateAuthorization>(ActionTypes.InvalidateAuthorization),
    filter(() => !!getStoredAuth()),
    map(() => getStoredAuth().auth.accessToken),
    tap(() => removeStoredAuthAndClient()),
    switchMap((token) => this._api
      .logout$(token)
      .pipe(catchError(() => EMPTY))
    )
  ), { dispatch: false });

  onInvalidateAuthorizationReset$ = createEffect(() => this._actions$.pipe(
    ofType<InvalidateAuthorization>(ActionTypes.InvalidateAuthorization),
    map(() => new Reset())
  ));

  onSetActiveClient$ = createEffect(() => this._actions$.pipe(
    ofType<SetActiveClient>(ActionTypes.SetActiveClient),
    tap(({ payload }) => saveSelectedClient(payload)),
    withLatestFrom(this._store.pipe(select(referer))),
    map(([ { meta }, refererPath ]) => {
      const path = meta
        ? [ refererPath ? refererPath : '/dashboard', { reload: true } ]
        : [ refererPath ? refererPath : '/dashboard' ];
      return new Go({ path });
    }),
    filter((action) => !!action)
  ));

  onSetActiveClientResetReferer$ = createEffect(() => this._actions$.pipe(
    ofType<SetActiveClient>(ActionTypes.SetActiveClient),
    map(() => new ResetReferer())
  ));

  onSendPasswordChangeRequest$ = createEffect(() => this._actions$.pipe(
    ofType<SendPasswordChangeRequest>(ActionTypes.SendPasswordChangeRequest),
    withLatestFrom(this._store.pipe(select(identity))),
    switchMap(([ _, { email } ]) => this._api
      .recoverPassword$(email)
      .pipe(
        map(() => new SendPasswordChangeLocalSuccess()),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new SendPasswordChangeFail(data)))
        )
      )
    )
  ));

  onSendPasswordChangeFail$ = createEffect(() => this._actions$.pipe(
    ofType<SendPasswordChangeFail>(ActionTypes.SendPasswordChangeFail),
    switchMap(({ errors }) => this._translate
      .get('common.close')
      .pipe((map((translation) => {
        const er = errors.add as Error;
        return {
          close: translation,
          message: er.data.message
        };
      })))
    ),
    switchMap(({ close, message }) => this._snack
      .fail$(message, close)
      .pipe(map(() => new RemoveError(ActionTypes.SendPasswordChangeFail)))
    )
  ));

  onSendPasswordChangeSuccess$ = createEffect(() => this._actions$.pipe(
    ofType<SendPasswordChangeLocalSuccess>(ActionTypes.SendPasswordChangeLocalSuccess),
    switchMap(() => this._translate
      .get([
        'common.close',
        'authBase.passwordChangeSuccessSnack'
      ])
      .pipe(
        switchMap((translations) => this._snack.success$(
          translations['authBase.passwordChangeSuccessSnack'],
          translations['common.close']
        ))
      )
    )
  ), { dispatch: false });

  // -- external auth
  // -- login
  onExternalAuthLoginRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthLoginRequest>(ActionTypes.ExternalAuthLoginRequest),
    switchMap(({ payload }) => this._api
      .externalAuthLogin$(payload)
      .pipe(
        map(({ authUrl }) => new ExternalAuthLoginLocalSuccess(authUrl)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ExternalAuthLoginFail(data)))
        )
      )
    )
  ));

  onExternalAuthLoginAuthRedirect$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthLoginLocalSuccess>(ActionTypes.ExternalAuthLoginLocalSuccess),
    delay(100),
    tap(({ payload }) => {
      window.location.href = payload;
    })
  ), { dispatch: false });

  onExternalAuthLoginFail$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthLoginFail>(ActionTypes.ExternalAuthLoginFail),
    switchMap((action) => this._snack.translatedFail$(
      action.payload.message,
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ExternalAuthLoginFail))
  ));

  // -- login validate
  onExternalAuthLoginValidateRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthLoginValidateRequest>(ActionTypes.ExternalAuthLoginValidateRequest),
    switchMap(({ payload }) => this._api
      .externalAuthLoginValidate$(payload.type, payload.code, payload.state)
      .pipe(
        map((res) => new ExternalAuthLoginValidateLocalSuccess(res)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ExternalAuthLoginValidateFail(data)))
        )
      )
    )
  ));

  onExternalAuthLoginValidateSuccessUpdateLocalStorage$ = createEffect(() => this._externalAuthLoginValidateSuccess$.pipe(
    tap(({ payload }) => updateStoredEntities(payload.entities))
  ), { dispatch: false });

  onExternalAuthLoginValidateSuccessAuthorize$ = createEffect(() => this._externalAuthLoginValidateSuccess$.pipe(
    map(({ payload }) => new Authorize(
      payload.entities,
      payload.userId,
      payload.auth
    ))
  ));

  onExternalAuthLoginValidateSuccessRedirect$ = createEffect(() => this._externalAuthLoginValidateSuccess$.pipe(
    map(() => new Go({ path: [ '/dashboard' ]}))
  ));

  onExternalAuthLoginValidateFail$ = createEffect(() => this._externalAuthLoginValidateFail$.pipe(
    switchMap((action) => this._snack.translatedFail$(
      action.payload.message,
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ExternalAuthLoginValidateFail))
  ));

  onExternalAuthLoginValidateShowHint$ = createEffect(() => this._externalAuthLoginValidateSuccess$.pipe(
    filter((d) => d.payload.showHint),
    delay(150),
    switchMap(() => this._translate
      .get([
        'common.close',
        'authBase.emailLoginDisabledHint'
      ])
      .pipe(
        switchMap((translations) => this._snack.info$(
          translations['authBase.emailLoginDisabledHint'],
          translations['common.close']
        ))
      )
    )
  ), { dispatch: false });

  onExternalAuthLoginValidateFailRedirect$ = createEffect(() => this._externalAuthLoginValidateFail$.pipe(
    map(() => new Go({ path: [ '/login' ]}))
  ));

  // -- connect
  onExternalAuthConnectRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthConnectRequest>(ActionTypes.ExternalAuthConnectRequest),
    switchMap(({ payload }) => this._api
      .externalAuthConnect$(payload)
      .pipe(
        map((res) => new ExternalAuthConnectLocalSuccess(res.authUrl, payload)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ExternalAuthConnectFail(data, payload)))
        )
      )
    )
  ));

  onExternalAuthConnectAuthRedirect$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthConnectLocalSuccess>(ActionTypes.ExternalAuthConnectLocalSuccess),
    delay(100),
    tap(({ payload }) => {
      window.location.href = payload;
    })
  ), { dispatch: false });

  onExternalAuthConnectFail$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthConnectFail>(ActionTypes.ExternalAuthConnectFail),
    switchMap((action) => this._snack.translatedFail$(
      action.payload.message,
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ExternalAuthConnectFail))
  ));

  // -- connect validate
  onExternalAuthConnectValidateRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthConnectValidateRequest>(ActionTypes.ExternalAuthConnectValidateRequest),
    switchMap(({ payload }) => this._api
      .externalAuthConnectValidate$(payload.type, payload.code, payload.state)
      .pipe(
        map((res) => new ExternalAuthConnectValidateLocalSuccess(res)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ExternalAuthConnectValidateFail(data)))
        )
      )
    )
  ));

  onExternalAuthConnectValidateSuccessUpdateLocalStorage$ = createEffect(() => this._externalAuthConnectValidateSuccess$.pipe(
    tap(({ payload }) => updateStoredEntities(payload.entities))
  ), { dispatch: false });

  onExternalAuthConnectValidateSuccessShowSnack$ = createEffect(() => this._externalAuthConnectValidateSuccess$.pipe(
    switchMap(() => this._snack.translatedSuccess$(
      'authBase.externalAuthValidationSuccess',
      'common.close'
    ))
  ), { dispatch: false });

  onExternalAuthConnectValidateSuccessRedirect$ = createEffect(() => this._externalAuthConnectValidateSuccess$.pipe(
    map(() => new Go({ path: [ '/profile' ]}))
  ));

  onExternalAuthConnectValidateFail$ = createEffect(() => this._externalAuthConnectValidateFail$.pipe(
    switchMap((action) => this._snack.translatedFail$(
      action.payload.message,
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ExternalAuthConnectValidateFail))
  ));

  onExternalAuthConnectValidateFailRedirect$ = createEffect(() => this._externalAuthConnectValidateFail$.pipe(
    map(() => new Go({ path: [ '/login' ]}))
  ));

  // -- remove
  onExternalAuthRemoveRequest$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthDeleteRequest>(ActionTypes.ExternalAuthDeleteRequest),
    switchMap(({ payload }) => this._api
      .externalAuthDelete$(payload)
      .pipe(
        map((res) => new ExternalAuthDeleteLocalSuccess(res, payload)),
        catchError((err) => this._errorTranslation
          .get$(err)
          .pipe(map((data) => new ExternalAuthDeleteFail(data, payload)))
        )
      )
    )
  ));

  onExternalAuthDeleteFail$ = createEffect(() => this._actions$.pipe(
    ofType<ExternalAuthDeleteFail>(ActionTypes.ExternalAuthDeleteFail),
    switchMap((action) => this._snack.translatedFail$(
      action.payload.message,
      'common.close'
    )),
    map(() => new RemoveError(ActionTypes.ExternalAuthDeleteFail))
  ));

  onExternalAuthDeleteSuccessShowSnack$ = createEffect(() => this._onExternalAuthDeleteSuccess$.pipe(
    switchMap(() => this._snack.translatedSuccess$(
      'authBase.externalAuthDeleteSuccess',
      'common.close'
    ))
  ), { dispatch: false });

  onExternalAuthDeleteSuccessUpdateLocalStorage$ = createEffect(() => this._onExternalAuthDeleteSuccess$.pipe(
    tap(({ payload }) => updateStoredEntities(payload.entities))
  ), { dispatch: false });

  constructor(
    private _actions$: Actions,
    private _api: AuthBaseApi,
    private _store: Store<State>,
    private _snack: SnackService,
    private _errorTranslation: ErrorTranslationService,
    private _translate: TranslateService
  ) {}
}
